// Taken from https://github.com/jbdemonte/vat-siren-siret and improved with typing

export type Siret = string & { __brand: 'SIRET' }
export type FrenchVAT = string & { __brand: 'FrenchVAT' }
export type Siren = string & { __brand: 'SIREN' }

const SIRET_REGEX = /^\d{14}$/
const SIREN_REGEX = /^\d{9}$/
const FRENCH_VAT_REGEX = /^FR\d{11}$/

export function getLuhnControlNumber(value: string): number {
  let digit = 0
  let sum = 0
  let even = true
  for (let i = value.length - 1; i >= 0; i -= 1) {
    digit = Number.parseInt(value[i], 10)
    if (even) {
      digit += digit
      if (digit > 9) {
        digit -= 9
      }
    }
    sum += digit
    even = !even
  }
  return (10 - (sum % 10)) % 10
}

export function checkLuhn(value: string): boolean {
  let digit = 0
  let sum = 0
  let even = false
  for (let i = value.length - 1; i >= 0; i -= 1) {
    digit = Number.parseInt(value[i], 10)
    if (even) {
      digit += digit
      if (digit > 9) {
        digit -= 9
      }
    }
    sum += digit
    even = !even
  }
  return sum % 10 === 0
}

export function getVATKey(siren: Siren): number {
  return (12 + 3 * (Number.parseInt(siren, 10) % 97)) % 97
}

export function formatSiret(input: string) {
  const digits = input.replace(/\D/g, '')
  const groups = [3, 3, 3, 5]
  let formatted = ''
  let index = 0

  for (const group of groups) {
    if (index >= digits.length) break
    if (formatted) formatted += ' '
    formatted += digits.slice(index, index + group)
    index += group
  }

  return formatted
}

export function parseSiret(input: string) {
  const siret = input.replace(/\D/g, '').slice(0, 14)
  return siret
}

export function isSIRET(siret: unknown): siret is Siret {
  return typeof siret === 'string' && SIRET_REGEX.test(siret) && checkLuhn(siret)
}

export function assertIsSiret(siret: unknown): asserts siret is Siret {
  if (!isSIRET(siret)) {
    throw new Error(`Invalid ${siret} SIRET`)
  }
}

export function isSIREN(siren: unknown): siren is Siren {
  return typeof siren === 'string' && SIREN_REGEX.test(siren) && checkLuhn(siren)
}

export function assertIsSIREN(siren: unknown): asserts siren is Siren {
  if (!isSIREN(siren)) {
    throw new Error(`Invalid ${siren} SIREN`)
  }
}

export function isFrenchVAT(vat: unknown): vat is FrenchVAT {
  if (typeof vat !== 'string') {
    return false
  }
  if (!FRENCH_VAT_REGEX.test(vat)) {
    return false
  }
  const siren = vat.slice(4)
  return (
    isSIREN(siren) &&
    getVATKey(siren) === Number.parseInt(vat.substring(2, 4), 10) &&
    checkLuhn(siren)
  )
}

export function toSIREN(value: Siren | Siret | FrenchVAT): Siren {
  if (typeof value !== 'string') {
    throw new Error('Invalid input')
  }
  if (isSIRET(value)) {
    return value.slice(0, 9) as Siren
  }
  if (isFrenchVAT(value)) {
    return value.slice(4) as Siren
  }
  if (isSIREN(value)) {
    return value
  }
  throw new Error('Invalid SIREN, SIRET or French VAT number')
}

export function toFrenchVAT(value: Siret | Siren | FrenchVAT): FrenchVAT {
  if (isFrenchVAT(value)) {
    return value
  }
  const siren = toSIREN(value)
  const key = getVATKey(siren)
  return `FR${key.toString().padStart(2, '0')}${siren}` as FrenchVAT
}
